import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { maintenancePageQuery, maintenanceTableQuery, type MaintenanceWhen, type WindowRow } from '../api/maintenance'
import { ApiError } from '../api/client'
import { moduleOf, shellQuery } from '../api/shell'
import { CmdbViews } from '../cmdb/CmdbViews'
import { esc } from '../cmdb/cells'
import { AdmPageHead } from '../softsee/adm/AdmPageHead'
import { DataTable, useTableState, type TableColumn } from '../ui/DataTable'
import { Forbidden } from '../ui/Forbidden'
import { toast } from '../ui/notify'
import { Content } from '../ui/Shell'
import { Spinner } from '../ui/Spinner'
import { usePageStyles } from '../ui/usePageStyles'
import { MaintenanceShapes, SCOPE_ICON } from './MaintenanceShapes'
import { WindowFormModal } from './WindowFormModal'

/**
 * `Maintenance::index` - ferestrele de mentenanta, la /app/maintenance.
 *
 * ADRESA E CEA DIN PORTAL, LA RADACINA
 *
 * Nu sub `/cmdb`, desi ecranul e al CMDB-ului si poarta banda lui: cele patru
 * vederi stau sub doua controllere de nivel intai, "pentru motive istorice care
 * nu merita desfacute" - scrie chiar in `inventoree/_views.php`. Mutata, ar fi
 * o adresa noua pe care n-o are scrisa nimeni.
 *
 * CE RASPUNDE ECRANUL
 *
 * "Cand a fost VOIE ca lucrul asta sa se schimbe." O schimbare se socoteste
 * autorizata daca a cazut inauntrul unei ferestre care acopera elementul, sau
 * daca cineva o leaga dupa aceea de un tichet. Fara ferestre, fiecare noapte de
 * patch-uri planificata iese pe lista de incalcari.
 *
 * Deci NU e o a doua lista de schimbari: constatarea - ce a ramas neautorizat -
 * se citeste la `/cmdb/health#admChanges`, si intr-acolo trimite butonul din
 * dreapta si randul din caseta albastra.
 *
 * SCRIEREA
 *
 * "Add a window" si clicul pe un rand deschid `WindowFormModal`
 * (`Maintenance::windowForm/saveWindow/removeWindow`); luna si ziua sunt
 * `MaintenanceCalendarPage` / `MaintenanceDayPage`, prin comutatorul de forma.
 */

const STYLES = ['css/adm.css']
const TABLE_ID = 'admWindowsTable'

/** Pastila si numele fiecarei stari, ca in portal. */
const STATE_PILL: Record<string, string> = { open: 'adm-pill-ok', upcoming: 'adm-pill-running', past: 'adm-pill' }
const STATE_LABEL: Record<string, string> = { open: 'Open now', upcoming: 'Upcoming', past: 'Finished' }

/** Filele de perioada, in ordinea din portal. `''` e "toate". */
const TABS: { value: MaintenanceWhen; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'open', label: 'Open now' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'past', label: 'Finished' },
]

function nameCell(row: WindowRow): string {
  return (
    '<strong>' +
    esc(row.name) +
    '</strong>' +
    (row.created_by !== ''
      ? '<div class="adm-muted" style="font-size:11px">set by ' + esc(row.created_by) + '</div>'
      : '')
  )
}

function scopeCell(row: WindowRow): string {
  return (
    '<i class="fas ' +
    (SCOPE_ICON[row.scope_type] ?? 'fa-cube') +
    ' adm-muted"></i> ' +
    esc(row.scope_name) +
    '<div class="adm-muted" style="font-size:11px">' +
    esc(row.scope_label) +
    '</div>'
  )
}

function ticketCell(row: WindowRow): string {
  /* Tichetul e ACOPERIREA ferestrei, si de-aia are coloana lui: o fereastra
     fara el a fost declarata de cineva, nu ceruta de nimeni. */
  return row.ticket_id > 0
    ? '<a href="/app/easeedesk/ticket/' + row.ticket_id + '">#' + row.ticket_id + '</a>'
    : '<span class="adm-muted">&mdash;</span>'
}

function stateCell(row: WindowRow): string {
  return '<span class="adm-pill ' + (STATE_PILL[row.state] ?? '') + '">' + esc(STATE_LABEL[row.state] ?? '') + '</span>'
}

function editCell(row: WindowRow): string {
  return '<button type="button" class="btn btn-default btn-xs windowEdit" data-id="' + row.ID + '"><i class="fa fa-pencil-alt"></i> Edit</button>'
}

export function windowColumns(canWrite = false): TableColumn[] {
  const as = (row: Record<string, unknown>) => row as unknown as WindowRow
  return [
    { index: 0, title: 'Window', orderable: true, visible: true, html: (r) => nameCell(as(r)) },
    { index: 1, title: 'Customer', orderable: true, visible: true, className: 'adm-muted', html: (r) => esc(as(r).company_name) },
    { index: 2, title: 'Covers', orderable: true, visible: true, html: (r) => scopeCell(as(r)) },
    { index: 3, title: 'From', orderable: true, visible: true, className: 'adm-nowrap', html: (r) => esc(as(r).starts_at) },
    { index: 4, title: 'To', orderable: true, visible: true, className: 'adm-nowrap', html: (r) => esc(as(r).ends_at) },
    { index: 5, title: 'Ticket', orderable: true, visible: true, html: (r) => ticketCell(as(r)) },
    {
      index: 6,
      /*
       * Nu se sorteaza: starea se socoteste din ceas, nu se tine in baza, deci
       * o sortare dupa ea ar cere un `CASE` tinut in pas cu socoteala. Filele
       * de deasupra raspund la aceeasi intrebare, si o duc si in adresa.
       */
      title: 'State',
      orderable: false,
      visible: true,
      html: (r) => stateCell(as(r)),
    },
    /* Butonul de editare, ca `windowEdit` din portal: clicul e prins pe
       document (delegat), fiindca celula e HTML scris de DataTables. */
    { index: 7, title: '', orderable: false, visible: canWrite, className: 'text-right adm-nowrap', html: (r) => editCell(as(r)) },
  ]
}

export function MaintenanceWindowsPage() {
  const shell = useQuery(shellQuery(moduleOf('/maintenance')))
  usePageStyles(STYLES, shell.data?.version ?? '')

  /*
   * Perioada sta in ADRESA, ca in portal: "filele sunt legaturi acum - un ecran
   * de modul are o adresa, iar un filtru care nu ajunge in bara de adrese e o
   * vedere pe care nimeni n-o poate trimite unui coleg".
   */
  const when = whenFromLocation()
  const page = useQuery(maintenancePageQuery(when))

  const [state, setState] = useTableState(TABLE_ID, {
    start: 0,
    length: 25,
    /* Cea mai noua fereastra intai: ce s-a planificat ultima data e ce cauta
       omul care tocmai a planificat-o. */
    order: { column: 3, dir: 'desc' },
    search: '',
  })

  const canWrite = page.data?.canWrite === true
  const columns = useMemo(() => windowColumns(canWrite), [canWrite])
  const [form, setForm] = useState<number | null>(null)

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const button = (e.target as HTMLElement).closest('.windowEdit')
      if (!button || !document.getElementById(TABLE_ID)?.contains(button)) return
      setForm(Number(button.getAttribute('data-id') ?? 0))
    }
    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, [])

  const table = useQuery({
    ...maintenanceTableQuery({
      draw: 1,
      start: state.start,
      length: state.length,
      search: state.search,
      when,
      order_column: state.order.column,
      order_dir: state.order.dir,
    }),
    placeholderData: keepPreviousData,
  })

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (!page.data) return <Spinner />

  /* Cifrele de deasupra vin cu randurile cand exista, ca sa nu ramana vechi
     dupa o pagina; la prima desenare sunt cele din antet. */
  const counts = table.data?.counts ?? page.data.counts

  if (!page.data.hasTable) {
    return (
      <Content>
        <CmdbViews views={page.data.views} current="maintenance" />
        <div className="alert alert-warning" style={{ margin: 20 }}>
          <h4>
            <i className="fas fa-database"></i> Maintenance windows
          </h4>
          <p>The maintenance window table is not in the database yet.</p>
          <p className="text-muted small" style={{ marginBottom: 0 }}>
            Until it is, every change made outside a ticket is reported as unauthorised.
          </p>
        </div>
      </Content>
    )
  }

  const rows = (table.data?.data ?? []) as unknown as Record<string, unknown>[]

  return (
    <Content>
      {/*
       * Banda celor patru vederi, PRIMUL copil al invelisului - ca pe
       * `/app/cmdb`, `/app/cmdb/health` si `/app/cmdb/baselines`.
       *
       * Sta sub cartonasul cu numele ecranului si asa a stat de la inceput:
       * componenta era chemata, serverul trimitea lista, si totusi omul a spus
       * ca ecranul n-are banda - fiindca pe celelalte trei ecrane ale
       * registrului ea e primul lucru din pagina, iar aici venea dupa un
       * cartonas inalt, intre doua panouri. O banda care isi muta locul de la
       * un ecran la altul nu mai e banda nimanui.
       *
       * Macheta veche (`inventoree/maintenance/index.php`) o pune dupa
       * `_pagehead`, si asa a fost portata. Cele patru ecrane ale registrului
       * se citesc insa unul dupa altul, deci se aliniaza intre ele: regula e
       * cea scrisa pe 17 septembrie in `CmdbHealthPage.test.tsx` - banda e
       * primul copil al lui `#content`.
       */}
      <CmdbViews views={page.data.views} current="maintenance" />

      <AdmPageHead
        tone="green-mode"
        icon="fa-calendar-check"
        title="Maintenance windows"
        note="when a change on these items was allowed to happen"
        stats={[
          { value: counts.total.toLocaleString('en-US'), label: 'windows', tone: 'grey' },
          { value: counts.open.toLocaleString('en-US'), label: 'open now', tone: counts.open > 0 ? 'green-mode' : 'grey' },
        ]}
        actions={
          <>
            {page.data.canWrite && (
              <button type="button" className="btn btn-primary btn-sm" id="windowAdd" onClick={() => setForm(0)}>
                <i className="fas fa-plus-circle"></i> Add a window
              </button>
            )}{' '}
            <a className="btn btn-default btn-sm" href="/app/cmdb/health#admChanges">
              <i className="fas fa-user-shield"></i> Unauthorised changes
            </a>
          </>
        }
      />

      {/*
       * Ecranul se explica singur, fiindca lucrul pe care il guverneaza sta in
       * alt ecran: cineva ajunge aici trimis de o cifra rosie de pe sanatatea
       * CMDB-ului, fara nicio idee ce e o fereastra.
       */}
      <div className="alert alert-info">
        <i className="fas fa-info-circle"></i> <strong>What this does.</strong> A change counts as authorised when it
        falls inside a window covering the item, or when somebody points it at the ticket that covers it. Without a
        window, every planned patch night is reported as a violation under{' '}
        <a href="/app/cmdb/health#admChanges">CMDB health</a>.
        <br />
        <br />
        The narrowest window wins: one item beats a class, a class beats the whole customer.
      </div>

      <div className="adm-section adm-section-green-mode">
        <h4>
          <i className="fas fa-calendar-check"></i> {counts.total.toLocaleString('en-US')} window(s)
        </h4>
        <span className="adm-section-note">newest first, a page at a time</span>

        <span className="adm-section-tools">
          <MaintenanceShapes active="list" dayDate={new Date().toISOString().slice(0, 10)} />{' '}
          {TABS.map((tab) => (
            <a
              key={tab.value}
              className={`btn btn-xs ${when === tab.value ? 'btn-primary' : 'btn-default'}`}
              href={tab.value === '' ? '/app/maintenance' : `/app/maintenance?when=${tab.value}`}
            >
              {tab.label}
            </a>
          ))}
        </span>
      </div>

      <DataTable
        id={TABLE_ID}
        columns={columns}
        rows={rows}
        total={table.data?.recordsFiltered ?? 0}
        fetching={table.isFetching}
        error={table.error ? table.error.message : null}
        onRetry={() => void table.refetch()}
        state={state}
        onState={setState}
        rowId={(row) => `window_${String(row.ID ?? 0)}`}
        emptyHint={
          when === ''
            ? 'No maintenance windows yet. Until there is one, every change outside a ticket is reported as unauthorised.'
            : 'Nothing in this period.'
        }
      />

      <WindowFormModal
        open={form !== null}
        id={form ?? 0}
        onClose={() => setForm(null)}
        onSaved={(message, warning) => toast(message, 'Maintenance windows', warning ? 'warning' : 'success')}
      />
    </Content>
  )
}

/**
 * Perioada din adresa, si numai una din cele trei.
 *
 * Orice altceva inseamna "toate" - acelasi raspuns pe care il da si serverul,
 * ca o adresa scrisa gresit sa arate lista intreaga, nu una goala pe care omul
 * ar citi-o ca "nu e nimic planificat".
 */
function whenFromLocation(): MaintenanceWhen {
  const value = new URLSearchParams(window.location.search).get('when') ?? ''

  return value === 'open' || value === 'upcoming' || value === 'past' ? value : ''
}
